import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { translations, getDir } from '@/lib/i18n';
import axios from 'axios';

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const MEDIA = (url) => url ? (url.startsWith('http') ? url : `${BACKEND_URL}${url}`) : '';

export const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('zayed_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('zayed_lang') || 'ar');
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem('zayed_user');
    return raw ? JSON.parse(raw) : null;
  });
  const [a11y, setA11y] = useState(() => ({
    fontSize: localStorage.getItem('zayed_fs') || 'base',
    highContrast: localStorage.getItem('zayed_hc') === '1',
  }));

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = getDir(lang);
    localStorage.setItem('zayed_lang', lang);
  }, [lang]);

  useEffect(() => {
    const html = document.documentElement;
    html.classList.remove('fs-lg', 'fs-xl', 'hc');
    if (a11y.fontSize === 'lg') html.classList.add('fs-lg');
    if (a11y.fontSize === 'xl') html.classList.add('fs-xl');
    if (a11y.highContrast) html.classList.add('hc');
    localStorage.setItem('zayed_fs', a11y.fontSize);
    localStorage.setItem('zayed_hc', a11y.highContrast ? '1' : '0');
  }, [a11y]);

  const t = useCallback((key) => translations[lang]?.[key] ?? key, [lang]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('zayed_token', data.token);
    localStorage.setItem('zayed_user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    localStorage.setItem('zayed_token', data.token);
    localStorage.setItem('zayed_user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('zayed_token');
    localStorage.removeItem('zayed_user');
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    lang, setLang, t, user, setUser, login, register, logout, a11y, setA11y,
  }), [lang, t, user, login, register, logout, a11y]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
